import { ChangeDetectorRef, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { HotToastService } from '@ngxpert/hot-toast';
import { untilDestroyed } from '@ngneat/until-destroy';
import { AlertType, ChangeRequestStatus, HeadlineType, SectionType } from '@core/enums';
import { AuthenticationService, CredentialsService } from '@auth';
import { BankEntity, ChangeRequest, ClubEntity, Credentials, MemberEntity, SharingInfoEntity } from '@core/entities';
import { FormService } from '@core/services';
import { UseMyProfile } from '@core/usecases/useMyProfile';
import { debounceTime, forkJoin } from 'rxjs';
import { ClearPushNotificationSeen, DateTimeUtility, GenerateImageUrlWithToken, GetHeadlineOption, mustMatch } from '@core/utils';
import { ButtonComponent } from '@shared/components/ui';
import { SafeUrl } from '@angular/platform-browser';

interface ProfileDTO {
  city: string;
  postCode: string;
  street: string;
  street2: string;
  email: string;
  phone: string;
  birthDate: string;
}

/**
 * MyProfileFormComposition
 *
 * This class is a composition class for MyProfileComponent.
 * Composition classes are responsible for handling the business logic of the component and the data that is being displayed in the component.
 *
 * It is also responsible for handling the form validation and submission.
 * It is also responsible for handling the API calls and the data that is being fetched from the API.
 * It is also responsible for handling the alert messages and the alert types.
 * It is also responsible for handling the password change and the bank change.
 * It is also responsible for handling the birthday sharing and the advertisement sharing.
 */

export class MyProfileComposition extends FormService {
  clubInfo: ClubEntity;
  memberDetails: MemberEntity;
  passwordChangeForm: FormGroup;
  shareInfoForm: FormGroup;
  // these two properties `userDetails` and `qrImage` are only used in the digital card component
  userDetails: Credentials = null;
  qrImage: SafeUrl;
  maskedIBANDisplayValue = '';
  isValidatingIBAN = false;

  public readonly _translate: TranslateService = inject(TranslateService);

  protected readonly _credService: CredentialsService = inject(CredentialsService);
  protected readonly _cdr: ChangeDetectorRef = inject(ChangeDetectorRef);

  private readonly _router: Router = inject(Router);
  private readonly _fb: FormBuilder = inject(FormBuilder);
  private readonly _authService: AuthenticationService = inject(AuthenticationService);
  private readonly _toastService: HotToastService = inject(HotToastService);
  private readonly _useMyProfile = new UseMyProfile();

  constructor() {
    super();

    this.userDetails = this._credService.credentials;
  }

  get isHeadlineEnterprise() {
    return GetHeadlineOption() === HeadlineType.ENTERPRISE;
  }

  get bankForm(): FormGroup {
    return this.form?.get('bankData') as FormGroup;
  }

  get bankChangeRequest(): ChangeRequest<BankEntity> {
    return this.memberDetails?.changeRequest?.bank;
  }

  get bankAlertType(): AlertType {
    return this._getAlertType(this.bankChangeRequest);
  }

  get isBankChangeRequestPending(): boolean {
    return this.bankChangeRequest?.status === ChangeRequestStatus.Pending;
  }

  get memberChangeRequest(): ChangeRequest<MemberEntity> {
    return this.memberDetails?.changeRequest?.member;
  }

  get memberAlertType(): AlertType {
    return this._getAlertType(this.memberChangeRequest);
  }

  get isMemberChangeRequestPending(): boolean {
    return this.memberChangeRequest?.status === ChangeRequestStatus.Pending;
  }

  get showMemberAlertCloseButton(): boolean {
    return this.memberAlertType === AlertType.REJECTED_REQUEST || this.memberAlertType === AlertType.APPROVED_REQUEST;
  }

  get showBankAlertCloseButton(): boolean {
    return this.bankAlertType === AlertType.REJECTED_REQUEST || this.bankAlertType === AlertType.APPROVED_REQUEST;
  }

  saveProfile(btnRef?: ButtonComponent): void {
    if (this.isMemberChangeRequestPending) {
      this._toastService.error(`${this._translate.instant('You already have a pending change request')}`);
      return;
    }
    if (!this.form?.valid) {
      this._toastService.error(`${this._translate.instant('Please fill all required fields')}`);
      this.form.markAllAsTouched();
      return;
    }
    const formData = this.removeEventFields(this.form.value);
    const originalData: MemberEntity = this.memberDetails;

    const changedFields: Partial<ProfileDTO> = {};

    // Check each field and only add changed ones
    if (this._valueChanged(originalData?.city, formData.city)) {
      changedFields.city = formData.city || null; // Ensure empty strings become null if needed
    }

    if (this._valueChanged(originalData?.postCode, formData.postCode)) {
      changedFields.postCode = formData.postCode || null;
    }

    if (this._valueChanged(originalData?.street, formData.street)) {
      changedFields.street = formData.street || null;
    }

    if (this._valueChanged(originalData?.street2, formData.street2)) {
      changedFields.street2 = formData.street2 || null;
    }

    if (this._valueChanged(originalData?.email, formData.email)) {
      changedFields.email = formData.email || null;
    }

    if (this._valueChanged(originalData?.phone, formData.phone)) {
      changedFields.phone = formData.phone || null;
    }

    if (this._valueChanged(originalData?.birthDate, formData.birthDate)) {
      changedFields.birthDate = formData.birthDate || null;
    }

    if (Object.keys(changedFields).length === 0) {
      this._toastService.info(`${this._translate.instant('No changes to save')}`);
      return;
    }

    this.call(this._useMyProfile.saveUserMemberProfile(changedFields as Partial<MemberEntity>), btnRef).then(
      () => {
        this._toastService.success(`${this._translate.instant('Update Successful')}`);
        this._disableFormFields();
        this.fetchData();
      },
      (err) => {
        if (typeof this.error === 'string') {
          this._toastService.error(this.error);
        } else {
          this._toastService.error(`${this._translate.instant('Something went wrong')}`);
        }
        this._disableFormFields();
      },
    );
  }

  saveBank(btnRef?: ButtonComponent) {
    if (this.isBankChangeRequestPending) {
      this._toastService.error(`${this._translate.instant('You already have a pending change request')}`);
      return;
    }
    if (!this.bankForm.valid || this.isValidatingIBAN) return;
    const body = this.bankForm.value;

    this.call(this._useMyProfile.saveBankInfo(body), btnRef).then(
      (res) => {
        if (res) {
          this._toastService.success(`${this._translate.instant('Update Successful')}`);
        }
        this._disableFormFields();
        this.fetchData();
      },
      (err) => {
        if (typeof this.error === 'string') {
          this._toastService.error(this.error);
        } else {
          this._toastService.error(`${this._translate.instant('Something went wrong')}`);
        }
        this.bankForm.get('iban').setValue(this.memberDetails?.bankData?.iban, { emitEvent: false });
        this.maskedIBANDisplayValue = this.maskIban(this.memberDetails?.bankData?.iban);
        this.bankForm.get('bic').setValue(this.memberDetails?.bankData?.bic, { emitEvent: false });
        this.bankForm.get('name').setValue(this.memberDetails?.bankData?.name, { emitEvent: false });
        this._disableFormFields();
      },
    );
  }

  changePassword(btnRef?: ButtonComponent): void {
    btnRef?.showLoading();
    const data = this.passwordChangeForm.value;
    this._useMyProfile
      .changeUserPassword(data)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (response) => {
          if (response) {
            this._authService
              .logout()
              .pipe(untilDestroyed(this))
              .subscribe({
                next: () => {
                  this._toastService.success(`${this._translate.instant('Password changed successfully. Please login again')}`);
                  this._credService.setCredentials();
                  ClearPushNotificationSeen();
                  btnRef?.showIdle();
                  this._router.navigate(['/login'], { queryParams: { returnUrl: this._router.url } });
                },
              });
          } else {
            btnRef?.showIdle();
          }
        },
        error: (e) => {
          this._toastService.error(e?.error?.message || `${this._translate.instant('Something went wrong')}`);
          btnRef?.showError();
        },
      });
  }

  fetchData(): void {
    const memberDetails$ = this._useMyProfile.getMemberProfile();
    const clubDetails$ = this._useMyProfile.getClubInfo();
    const sharingInfo$ = this._useMyProfile.getBirthdayAdvertisementSharing();

    this.isLoading = true;
    this.memberDetails = null;
    this.clubInfo = null;

    forkJoin([memberDetails$, clubDetails$, sharingInfo$])
      .pipe(untilDestroyed(this))
      .subscribe({
        next: async ([member, club, sharingInfo]) => {
          if (member?.membershipStartDate) member.membershipStartDate = DateTimeUtility.formatDateToString(member.membershipStartDate);
          await this.updateAndValidate(this.form, member, null, false);
          this.memberDetails = member;
          this.clubInfo = club;
          this._checkSharingInfo(sharingInfo);
          this._checkOfRequestedChanges(member);
          this.isLoading = false;

          this._bindFormChanges();
        },
      });
  }

  initForm(): void {
    this.form = this._fb.group({
      lastName: [''],
      firstName: [''],
      birthDate: ['', [Validators.required]],
      titleId: [''],
      title: [''],
      salutationId: [''],
      salutation: [''],
      letterSalutationId: [''],
      letterSalutation: [''],
      maritalStatusId: [''],
      maritalStatus: [''],
      emergencyContact: [''],
      emergencyPhone: [''],
      userRole: [{ value: '', disabled: true }],
      gender: [''],
      pobox: [''],
      poboxPostCode: [''],
      street: ['', [Validators.required]],
      street2: [''],
      poboxCity: [''],
      postCode: ['', [Validators.required, Validators.pattern('^[0-9]{5}(?:-[0-9]{4})?$')]],
      city: ['', [Validators.required]],
      country: [''],
      countryCode: [''],
      membershipNumber: [{ value: '', disabled: true }],
      membershipStartDate: [{ value: '', disabled: true }],
      bankData: this._fb.group({
        owner: [''],
        name: [''],
        iban: ['', [Validators.pattern('^[A-Z0-9]{22}$')]],
        bic: ['', [Validators.pattern('^[A-Z0-9]{11}$')]],
      }),
      email: ['', [Validators.required]],
      phone: ['', [Validators.required, Validators.pattern('^[0-9-+()_{}]*$')]],
      memberPhoto: [''],
      moreInfo: [''],
    });

    this.passwordChangeForm = this._fb.group(
      {
        oldPassword: ['', [Validators.required]],
        newPassword: ['', [Validators.required]],
        confirmPassword: ['', [Validators.required]],
      },
      { validators: mustMatch('newPassword', 'confirmPassword') },
    );

    this.shareInfoForm = this._fb.group({
      shareBirthday: [false],
      allowAdvertis: [false],
    });
  }

  maskIban(iban: string): string {
    if (!iban || iban.length <= 4) return iban;
    return '*'.repeat(iban.length - 4) + iban.slice(-4);
  }

  onFocus(event: FocusEvent): void {
    if (this.isBankChangeRequestPending) return;
    const input = event.target as HTMLInputElement;
    input.value = this.form.get('bankData.iban')?.value || '';
  }

  onBlur(event: FocusEvent): void {
    if (this.isBankChangeRequestPending) return;
    const input = event.target as HTMLInputElement;
    const fullIban = input.value;
    const ibanControl = this.form.get('bankData.iban');
    ibanControl?.setValue(fullIban);
    ibanControl?.markAsTouched();
    ibanControl?.markAsDirty();
    this.maskedIBANDisplayValue = this.maskIban(fullIban);
    input.value = this.maskedIBANDisplayValue;
  }

  // ---------------------------------------------------------------------------------------------------------------------
  // ------------------------------------------ Private Protected Functions ----------------------------------------------
  // ---------------------------------------------------------------------------------------------------------------------

  protected _fetchMemberDetails(): void {
    this._useMyProfile
      .getMemberProfile()
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (member) => {
          this.memberDetails = member;
          this.isLoading = false;
        },
      });
  }

  protected _getUserDetails() {
    if (this.userDetails && (!this.userDetails.firstName || !this.userDetails.lastName)) {
      window.location.href = '/logout';
    }

    if (this.userDetails && this.userDetails.qrcodeUrl) {
      this.qrImage = GenerateImageUrlWithToken(this.userDetails?.userId, SectionType.QR_CODE);
      // this.qrImage = this._imageService.convertBase64ToBlobUrl(qrBase64Image.substring(20));
    } else {
      console.log('No QR code found');
    }
  }

  private _bindFormChanges(): void {
    // bind to shareBirthday and allowAdvertis
    this.shareInfoForm
      .get('shareBirthday')
      .valueChanges.pipe(untilDestroyed(this))
      .subscribe({
        next: (value) => {
          const shareBirthday = value ? 0 : 1;
          this.call(this._useMyProfile.saveSharingBirthday(shareBirthday)).then(() => {
            this._toastService.success(`${this._translate.instant('Update Successful')}`);
          });
        },
      });

    this.shareInfoForm
      .get('allowAdvertis')
      .valueChanges.pipe(untilDestroyed(this))
      .subscribe({
        next: (value) => {
          const allowAdvertis = value ? 0 : 1;
          this.call(this._useMyProfile.saveAdvertisementView(allowAdvertis)).then(() => {
            this._toastService.success(`${this._translate.instant('Update Successful')}`);
            const creds = this._credService.credentials;
            creds.allowAdvertisement = allowAdvertis;
            this._credService.setCredentials(creds);
          });
        },
      });

    this.bankForm
      .get('iban')
      .valueChanges.pipe(untilDestroyed(this))
      .pipe(debounceTime(500))
      .subscribe({
        next: (iban: any) => {
          if (iban) {
            this.isValidatingIBAN = true;
            this._validateIBANAndUpdate();
          }
        },
      });

    // These are to set the display value of the iban field
    const originalIban = this.form.get('bankData.iban')?.value || '';
    this.maskedIBANDisplayValue = this.maskIban(originalIban);

    if (this.isBankChangeRequestPending) {
      this.bankForm.disable();
      this.bankForm?.get('iban')?.disable();
    }
  }

  private _disableFormFields(): void {
    this.form.get('userRole').disable();
    this.form.get('membershipStartDate').disable();
    this.form.get('membershipNumber').disable();
  }

  private _checkOfRequestedChanges(memberDetails: MemberEntity): void {
    const { changeRequest } = memberDetails;
    if (changeRequest) {
      if (changeRequest.member && changeRequest.member.status !== ChangeRequestStatus.Rejected && changeRequest.member.dataChanges) {
        this.updateAndValidate(this.form, changeRequest.member.dataChanges).then();
      }
      if ((changeRequest.bank && changeRequest.bank.status !== ChangeRequestStatus.Rejected, changeRequest.bank.dataChanges)) {
        this.updateAndValidate(this.form, changeRequest.bank.dataChanges).then();
      }
    }

    // Explicitly mark the form as touched to show the validation errors
    this.form.markAllAsTouched();
  }

  private _getAlertType(changeRequest: ChangeRequest<MemberEntity | BankEntity>) {
    if (!changeRequest) return null;
    switch (changeRequest?.status) {
      case ChangeRequestStatus.Pending:
        return AlertType.PENDING_REQUEST;
      case ChangeRequestStatus.Rejected:
        return AlertType.REJECTED_REQUEST;
      case ChangeRequestStatus.Accepted:
        return AlertType.APPROVED_REQUEST;
      default:
        return null;
    }
  }

  private _checkSharingInfo(sharingInfo: SharingInfoEntity): void {
    if (sharingInfo) {
      this.shareInfoForm.get('shareBirthday').setValue(sharingInfo.shareBirthdayBool, {
        emitEvent: false,
      });
      this.shareInfoForm.get('allowAdvertis').setValue(sharingInfo.allowAdvertisementBool, {
        emitEvent: false,
      });
    }
  }

  private _validateIBANAndUpdate(): void {
    this.isValidatingIBAN = true;
    const iban = this.bankForm.get('iban').value;
    this._useMyProfile
      .validateIBAN(iban)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (response) => {
          if (response && response.valid) {
            this.bankForm.get('iban').setErrors(null);
            this.bankForm.get('bic').setValue(response.bic, { emitEvent: false });
            this.bankForm.get('name').setValue(response.name, { emitEvent: false });
          } else {
            this.bankForm.get('iban').setErrors({ invalid: true });
            this._toastService.error(response.message, { duration: 10000 });
          }
        },
        error: (e) => {
          this._toastService.error(e?.error?.message || `${this._translate.instant('Something went wrong')}`);
        },
        complete: () => {
          this.isValidatingIBAN = false;
        },
      });
  }

  // Helper function to safely compare values accounting for different types
  private _valueChanged(original: any, current: any): boolean {
    // If both values are empty (null, undefined, empty string), consider them equal
    if ((original == null || original === '') && (current == null || current === '')) return false;

    // If only one value is empty, they are different
    if (original == null || original === '' || current == null || current === '') return true;

    // Convert to strings for comparison if needed
    const originalStr = typeof original === 'string' ? original.trim() : String(original).trim();
    const currentStr = typeof current === 'string' ? current.trim() : String(current).trim();

    return originalStr !== currentStr;
  }
}
